import * as React from 'react';
import { useFetch } from 'use-http';
import { 
  FormSelect, 
  FormSelectOption, 
  Alert, 
  Spinner,
  Select,
  SelectList,
  SelectOption,
  MenuToggle,
  MenuToggleElement,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
  Button
} from '@patternfly/react-core';
import { TimesIcon } from '@patternfly/react-icons';
import { GeneralSettingsContext } from '@app/Settings/General/GeneralSettings';

type FetchSelectProps = {
  url: string;
  label: string;
  placeholderText: string;
  mapItems: (json) => Array<string>; // take the json resp and give back selection items.
  onSelect: (value: string) => void;
};

const FetchSelect: React.FunctionComponent<FetchSelectProps> = ({
  url,
  label,
  placeholderText,
  mapItems,
  onSelect,
}) => {
  //
  // This generally works but for the fact that the initial selection
  // does not invoke onSelect() so the parent does not get it. The user
  // is forced to choose another option then the first again if the
  // first choice is the desired one.
  //
  const options = {
    credentials: 'include',
    cachePolicy: 'no-cache',
  };
  const { loading, error, get, response } = useFetch(url, options, []);
  const [value, setValue] = React.useState<string>('');
  const conf = React.useContext(GeneralSettingsContext);
  const [strings, setStrings] = React.useState<Array<string>>([]);

  const localOnSelect = (v) => {
    setValue(v);
    onSelect(v);
  };

  const load = React.useCallback(async () => {
    const resp = await get();
    if (response.ok) {
      setStrings(mapItems(resp));
    }
  }, [get, response]); // do not add mapItems here. triggers refresh.

  React.useEffect(() => {
    load();
  }, [load]);

  if (error) {
    const href = conf.auth + '/.login/web/auth/login?return_to=' + window.location.href;
    window.location.href = href;
  }

  return (
    <React.Fragment>
      {error && (
        <Alert variant="danger" title="Error">
          Error loading {label}
        </Alert>
      )}
      {loading && <Spinner size="sm" />}
      <FormSelect
        value={value}
        onChange={(_event, v) => localOnSelect(v)}
        aria-label={label}
        // isDisabled={strings.length === 0}
      >
        <FormSelectOption
          label={placeholderText ? placeholderText : 'Choose an entry'}
          key={0}
          isDisabled={true}
          isPlaceholder={true}
        >
          Choose an entry
        </FormSelectOption>
        {strings.sort().map((v, i) => (
          <FormSelectOption key={i} value={v} label={v}>
            {v}
          </FormSelectOption>
        ))}
      </FormSelect>
    </React.Fragment>
  );
};

type FetchSelectMultiProps = {
  url: string;
  label: string;
  mapItems: (json) => Array<string>;
  onSelect: (value: Array<string>) => void;
};

// see https://blog.logrocket.com/how-to-get-previous-props-state-with-react-hooks/
const usePrevious = (value) => {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const FetchSelectMulti: React.FunctionComponent<FetchSelectMultiProps> = (props) => {
  const opts = { credentials: 'include' };
  const { loading, error, data } = useFetch(props.url, opts, []);
  const [values, setValues] = React.useState<string[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [filterValue, setFilterValue] = React.useState('');
  const [selectOptions, setSelectOptions] = React.useState<string[]>([]);
  const [focusedItemIndex, setFocusedItemIndex] = React.useState<number | null>(null);
  const [activeItem, setActiveItem] = React.useState<string | null>(null);
  const textInputRef = React.useRef<HTMLInputElement>();

  const prevVals = usePrevious(values);

  React.useEffect(() => {
    if (data) {
      setSelectOptions(props.mapItems(data).sort());
    }
  }, [data, props]);

  React.useEffect(() => {
    let newSelectOptions: string[] = selectOptions;

    // Filter menu items based on the text input value when one exists
    if (filterValue) {
      newSelectOptions = selectOptions.filter((menuItem) =>
        String(menuItem).toLowerCase().includes(filterValue.toLowerCase())
      );

      // When no options are found after filtering, display 'No results found'
      if (!newSelectOptions.length) {
        newSelectOptions = ['No results found'];
      }

      // Open the menu when the input value changes and the new value is not empty
      if (!isOpen) {
        setIsOpen(true);
      }
    }

    setSelectOptions(newSelectOptions);
    setActiveItem(null);
    setFocusedItemIndex(null);
  }, [filterValue, isOpen, selectOptions]);

  const onToggleClick = () => {
    setIsOpen(!isOpen);
  };

  const onSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, itemId: string | number | undefined) => {
    const selection = itemId as string;
    
    if (selection && selection !== 'No results found') {
      let newValues: string[];
      
      if (values.includes(selection)) {
        // Remove selection
        newValues = values.filter((item) => item !== selection);
      } else {
        // Add selection
        newValues = [...values, selection];
      }
      
      setValues(newValues);
      props.onSelect(newValues);
    }
    
    setIsOpen(false);
    setFilterValue('');
  };

  const onTextInputChange = (_event: React.FormEvent<HTMLInputElement>, value: string) => {
    setInputValue(value);
    setFilterValue(value);
  };

  const onClear = () => {
    setValues([]);
    setInputValue('');
    setFilterValue('');
    props.onSelect([]);
    textInputRef?.current?.focus();
  };

  const removeSelection = (selection: string) => {
    const newValues = values.filter((item) => item !== selection);
    setValues(newValues);
    props.onSelect(newValues);
  };

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      variant="typeahead"
      onClick={onToggleClick}
      innerRef={toggleRef}
      isExpanded={isOpen}
      isDisabled={loading}
      isFullWidth
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={inputValue}
          onClick={onToggleClick}
          onChange={onTextInputChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setIsOpen(!isOpen);
            }
          }}
          id="typeahead-select-input"
          autoComplete="off"
          innerRef={textInputRef}
          placeholder={props.label}
          {...(activeItem && { 'aria-activedescendant': activeItem })}
          role="combobox"
          isExpanded={isOpen}
          aria-controls="select-typeahead-listbox"
        />
        <TextInputGroupUtilities>
          {!!inputValue && (
            <Button icon={<TimesIcon aria-hidden />}
              variant="plain"
              onClick={() => {
                setInputValue('');
                setFilterValue('');
                textInputRef?.current?.focus();
              }}
              aria-label="Clear input value"
             />
          )}
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  // Display selections as chips in the toggle
  const displaySelections = () => {
    if (values.length === 0) {
      return props.label;
    }
    return `${values.length} item${values.length > 1 ? 's' : ''} selected`;
  };

  return (
    <React.Fragment>
      {error && (
        <Alert variant="danger" title="Error">
          Error loading {props.label}
        </Alert>
      )}
      {loading && <Spinner size="sm" />}
      {data && (
        <>
          <Select
            id="multi-typeahead-select"
            isOpen={isOpen}
            selected={values}
            onSelect={onSelect}
            onOpenChange={() => {
              setIsOpen(false);
            }}
            toggle={toggle}
          >
            <SelectList id="select-typeahead-listbox">
              {selectOptions.map((option, index) => (
                <SelectOption
                  key={option || index}
                  isFocused={focusedItemIndex === index}
                  className={option === 'No results found' ? 'pf-m-aria-disabled' : ''}
                  onClick={() => option === 'No results found' && event?.preventDefault()}
                  id={`select-typeahead-${option.replace(' ', '-')}`}
                  {...(option === 'No results found' && { isAriaDisabled: true })}
                  value={option}
                  ref={null}
                >
                  {option}
                </SelectOption>
              ))}
            </SelectList>
          </Select>
          
          {/* Display selected items */}
          {values.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              {values.map((selection) => (
                <span
                  key={selection}
                  style={{
                    display: 'inline-block',
                    margin: '2px',
                    padding: '4px 8px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  {selection}
                  <Button icon={<TimesIcon style={{ fontSize: '10px' }} />}
                    variant="plain"
                    onClick={() => removeSelection(selection)}
                    style={{ marginLeft: '4px', padding: '0', minWidth: 'auto' }}
                    aria-label={`Remove ${selection}`}
                   />
                </span>
              ))}
              <Button
                variant="link"
                onClick={onClear}
                style={{ marginLeft: '8px', fontSize: '12px' }}
              >
                Clear all
              </Button>
            </div>
          )}
        </>
      )}
    </React.Fragment>
  );
};

export { FetchSelect, FetchSelectMulti };